import { Swatch } from '@tvg/fuse-design/swatch';
import withPrefetch from 'client/hoc/withPrefetch';
import { stringToChars } from 'client/utils/utils';
import { PLPGeneralFacetValue } from 'common/types/plp-data';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import Checkbox from './Checkbox';
import { colourConfig } from './ColourFacet/colourConfig';
import { CountStyled } from './CountStyled/CountStyled';
import Fieldset from './Fieldset';
import { SEOAnchorStyled } from './SEOAnchorStyled/SEOAnchorStyled';

const SEOAnchor = withPrefetch(SEOAnchorStyled);

export interface CheckboxGroupProps {
  groupDescription: string;
  items: PLPGeneralFacetValue[];
  /**
   * @default ''
   */
  className?: string;
  /**
   * @default undefined
   */
  dataTestId?: string;
  /**
   * @default false
   */
  error?: boolean;
  /**
   * @default false
   */
  isFullWidth?: boolean;
  isShowCheckbox?: boolean;
  isSwatch?: boolean;
  groupID?: string;
  /**
   * @default () => {}
   */
  onChange: (event: React.FormEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
}

const CategoryStyled = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0 0 var(--spacing-space24);
  padding: 0;
  color: var(--color-global-black);
  font-size: var(--font-size-1);
  line-height: var(--font-line-height-1);
  border: 0;
  background: none;
  width: 100%;
`;

const StyledSwatch = styled(React.memo(Swatch))<{ colour: string; size: string }>`
  background: ${({ colour }: { colour: string }) => colour};
  margin-right: var(--spacing-space4);
`;
const StyledSwatchLabel = styled.span`
  text-transform: capitalize;
`;

function CheckboxGroup({
  className,
  dataTestId: groupTestId,
  error,
  groupDescription,
  items,
  onChange,
  onKeyDown,
  isFullWidth,
  isSwatch,
  isShowCheckbox = true,
  groupID,
}: CheckboxGroupProps) {
  const classNameFull = className || 'facet-checkbox';
  const processedGroupID = useMemo(() => (groupID ? stringToChars(groupID) : undefined), [groupID]);

  return isShowCheckbox ? (
    <Fieldset
      className={`fuse-checkbox-group ${classNameFull}`}
      dataTestId={groupTestId}
      onChangeCallback={onChange}
      legend={groupDescription}
    >
      {items.map(({ name, id, count, url, selected }) => (
        <SEOAnchor href={url} key={name} className="forSEO" tabIndex={0} onKeyDown={onKeyDown}>
          <Checkbox
            error={!!error}
            name={name}
            value={id}
            dataTestId={groupTestId}
            className={classNameFull}
            fullWidth={isFullWidth}
            onChange={onChange}
            aria-label={`${name}, item count: ${count}`}
            componentId={processedGroupID}
            checked={selected}
            tabIndex={-1}
          >
            {isSwatch && (
              <>
                <StyledSwatch
                  colour={colourConfig[name.toLowerCase()] || name}
                  data-testid="facet-swatch"
                  size="large"
                />
                <StyledSwatchLabel data-testid="facet-checkbox__label--swatch">{name.toLowerCase()}</StyledSwatchLabel>
              </>
            )}
            {!isSwatch && <span data-testid="facet-checkbox__label">{name}</span>}
            <CountStyled data-testid="facet-checkbox__count">{count}</CountStyled>
          </Checkbox>
        </SEOAnchor>
      ))}
    </Fieldset>
  ) : (
    <div>
      {items.map(({ name, id, count, url }) => (
        <SEOAnchor
          href={url}
          key={name}
          className="forSEO"
          data-value={id}
          onKeyDown={onKeyDown}
          aria-label={`${name} item count: ${count}`}
        >
          <CategoryStyled
            onClick={onChange}
            data-value={id}
            data-label={name}
            data-testid={groupTestId}
            className={`${classNameFull} facet-category`}
          >
            <span data-testid="facet-checkbox__label">{name}</span>
            <CountStyled data-testid="facet-checkbox__count">{count}</CountStyled>
          </CategoryStyled>
        </SEOAnchor>
      ))}
    </div>
  );
}

export default React.memo(CheckboxGroup);
